import { useEffect, useRef } from 'react';

import { useEditionAwareness } from '../services/edition-awareness-context';

import { useAnalytics } from './use-analytics';

/**
 * One off event which tracks when a customer is eligible/ineligible for edition awareness.
 */
function useTrackAudienceAndTiming({
	isEligible,
	shouldTrack,
}: {
	isEligible: boolean;
	shouldTrack?: boolean;
}) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (!shouldTrack || hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'operational',
			actionSubject: 'audienceAndTiming',
			actionSubjectId: 'editionAwareness',
			action: 'checked',
			attributes: {
				isEligible,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, isEligible, shouldTrack]);
}

/**
 * Sends an ineligible audience and timing event only for free, in-trial, or in-predunning customers.
 */
export function IneligibleAnalytics(): null {
	const { edition, isInTrial, isInPredunning } = useEditionAwareness();
	const shouldTrack = Boolean(edition === 'free' || isInTrial || isInPredunning);

	useTrackAudienceAndTiming({ isEligible: false, shouldTrack });

	return null;
}
